import { cn } from "@/domains/library"

export interface PriceInputViewProps {
  price: string
  marketPrice: number | undefined
  onPriceChange: (price: string) => void
  onUseMarketPrice: () => void
  useMarketPrice: boolean
  isLoadingMarketPrice?: boolean
}

/**
 * Pure View component for price input
 * Matches swap UI visual style
 * Shows manual price input with option to use market price
 * Stateless - all data passed via props
 * Storybook-ready
 */
export function PriceInputView({
  price,
  marketPrice,
  onPriceChange,
  onUseMarketPrice,
  useMarketPrice,
  isLoadingMarketPrice = false,
}: PriceInputViewProps) {
  const marketPriceDisplay = marketPrice !== undefined ? marketPrice.toFixed(6) : "Calculating..."

  // Dynamic Font Size Logic (matching swap)
  const getFontSize = (value: string | undefined): string => {
    if (!value) return "text-4xl"
    const length = value.length
    if (length > 18) return "text-xl"
    if (length > 12) return "text-2xl"
    if (length > 9) return "text-3xl"
    return "text-4xl"
  }

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-p400-light bg-p400 p-4 transition-all focus-within:border-p100 hover:border-p300-light",
      )}
    >
      {/* Header Row: Label + Market Price Button */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-bold tracking-wide text-t100">Initial Price</span>
        {marketPrice !== undefined && (
          <button
            onClick={onUseMarketPrice}
            disabled={useMarketPrice}
            className={cn(
              "rounded-lg px-3 py-1.5 text-xs font-medium transition-all",
              useMarketPrice
                ? "cursor-not-allowed bg-p300 text-t200"
                : "bg-p300-light text-t100 hover:bg-p300-light/80",
            )}
          >
            {useMarketPrice ? "Using market price" : `Use market: ${marketPriceDisplay}`}
          </button>
        )}
      </div>

      {/* Main Row: Price Input */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <input
            type="text"
            inputMode="decimal"
            value={useMarketPrice ? marketPriceDisplay : price}
            onChange={(e) => {
              const val = e.target.value
              if (val === "" || /^\d*\.?\d*$/.test(val)) {
                // Limit to 6 decimal places
                const parts = val.split(".")
                if (parts.length > 1 && parts[1] && parts[1].length > 6) {
                  return
                }
                onPriceChange(val)
              }
            }}
            placeholder={isLoadingMarketPrice ? "Calculating..." : "0"}
            disabled={useMarketPrice || isLoadingMarketPrice}
            className={cn(
              "w-full bg-transparent font-bold text-t100 outline-none placeholder:text-t300",
              getFontSize(useMarketPrice ? marketPriceDisplay : price),
              "transition-all duration-200",
              (useMarketPrice || isLoadingMarketPrice) && "cursor-not-allowed opacity-60",
            )}
          />
          {marketPrice !== undefined && !useMarketPrice && price && Number(price) > 0 && (
            <div className="mt-1 text-xs text-t200">
              {Number(price) > marketPrice
                ? `+${(((Number(price) - marketPrice) / marketPrice) * 100).toFixed(2)}% vs market`
                : `${(((marketPrice - Number(price)) / marketPrice) * 100).toFixed(2)}% vs market`}
            </div>
          )}
          {isLoadingMarketPrice && <div className="mt-1 text-xs text-t300">Calculating market price...</div>}
        </div>
      </div>

      {/* Helper Text */}
      <div className="mt-3 text-xs text-t300">
        Set the starting exchange rate for both tokens. This rate reflects the initial market price.
      </div>
    </div>
  )
}
